import React, { useEffect, useState } from "react";
import jsonExport from "jsonexport/dist";

import {
  useMediaQuery,
  Card,
  Grid,
  Hidden,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";

import {
  downloadCSV,
  useListContext,
  BulkActionsToolbar,
  Datagrid,
  DateField,
  DateInput,
  FilterButton,
  ListContextProvider,
  ListToolbar,
  NumberField,
  RefreshButton,
  SelectInput,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  BulkExportButton,
} from "react-admin";

import { ChevronRightIcon, ExportIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { BackButton } from "../../../components/common/BackButton";
import { InvoiceStatus } from "../../../utils/constants";
import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { InvoiceStatusIcon } from "../../resources/invoices/InvoiceStatusIcon";
import { InvoicePeak } from "../../resources/invoices/InvoicePeak";
import { getServiceDate } from "../../resources/invoices/InvoiceUtils";

const useStyles = makeStyles((theme) => ({
  bulkActionsDisplayed: {
    marginTop: -theme.spacing(8),
    transition: theme.transitions.create("margin-top"),
  },
  totals: {
    marginLeft: theme.spacing(2),
  },
}));

const tabs = [
  { id: InvoiceStatus.NEW, name: "Draft" },
  { id: InvoiceStatus.PENDING, name: "Pending" },
  { id: InvoiceStatus.PAID, name: "Completed" },
];

const exporter = (invoices) => {
  const handleUndefined = (item) => (item ? item : "");

  const rows = [];

  invoices.forEach((invoice) => {
    const lineItems = invoice.lineItems?.items || [];

    lineItems.forEach((item) => {
      rows.push({
        InvoiceNo: invoice.invoiceNumber,
        Customer: invoice.accountName,
        InvoiceDate: new Date(invoice.createdAt).toLocaleDateString(),
        DueDate: new Date(invoice.dueDate).toLocaleDateString(),
        Terms: invoice.paymentTerms || "",
        Memo: handleUndefined(invoice.notes),
        "Item(Product/Service)": item.service?.name || "",
        ItemDescription: handleUndefined(item.serviceNote),
        ItemQuantity: item.quantity,
        ItemRate: item.unitCost,
        ItemAmount: item.amount,
        ItemTaxCode: item.taxRate?.taxCode || "",
        ItemTaxAmount: item.itemTax || "",
        Currency: "USD",
        ServiceDate: getServiceDate(invoice, item),
      });
    });

    // In case there are no line items, add one row with only invoice info
    if (lineItems.length === 0) {
      rows.push({
        InvoiceNo: invoice.invoiceNumber,
        Customer: invoice.accountName,
        InvoiceDate: new Date(invoice.createdAt).toLocaleDateString(),
        DueDate: new Date(invoice.dueDate).toLocaleDateString(),
        Terms: invoice.paymentTerms || "",
        Memo: handleUndefined(invoice.notes),
        "Item(Product/Service)": "",
        ItemDescription: "",
        ItemQuantity: "",
        ItemRate: "",
        ItemAmount: "",
        ItemTaxCode: "",
        ItemTaxAmount: "",
        Currency: "USD",
        ServiceDate: "",
      });
    }
  });

  jsonExport(
    rows,
    {
      rename: [
        "InvoiceNo",
        "Customer",
        "InvoiceDate",
        "DueDate",
        "Terms",
        "Memo",
        "Item(Product/Service)",
        "ItemDescription",
        "ItemQuantity",
        "ItemRate",
        "ItemAmount",
        "ItemTaxCode",
        "ItemTaxAmount",
        "Currency",
        "ServiceDate",
      ],
    },
    (err, csv) => {
      if (err) {
        console.error("Export error:", err);
      } else {
        downloadCSV(csv, `invoices_${new Date().toLocaleDateString()}`);
      }
    }
  );
};

const InvoiceSearchField = (props) => {
  const { label, alwaysOn } = props;
  const { displayedFilters, filterValues, setFilters } = useListContext(props);

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { accountName: { matchPhrasePrefix: `${value}` } },
            { invoiceNumber: { matchPhrasePrefix: `.*${value}.*` } },
            { invoiceNumber: { wildcard: `*${value}*` } },
            { poNumbers: { matchPhrasePrefix: `${value}` } },
            { poNumbers: { wildcard: `*${value}*` } },
            { railCarNumbers: { matchPhrasePrefix: `${value}` } },
            { railCarNumbers: { wildcard: `*${value}*` } },
            { pickupNumbers: { matchPhrasePrefix: `${value}` } },
            { pickupNumbers: { wildcard: `*${value}*` } },
          ],
        },
        displayedFilters
      );
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = () => {
  return [
    <InvoiceSearchField label="Search" alwaysOn />,
    <SelectInput
      source="status.eq"
      label="Status"
      variant="outlined"
      resettable
      alwaysOn
      choices={tabs || []}
      optionValue="id"
      emptyText="Show All"
    />,
    <DateInput
      source="createdAt.lte"
      label="Issued Before..."
      variant="outlined"
      resettable
      onKeyPress={(event) => event.preventDefault()}
    />,
    <DateInput
      source="createdAt.gte"
      label="Issued After..."
      variant="outlined"
      resettable
      onKeyPress={(event) => event.preventDefault()}
    />,
    <DateInput
      source="dueDate.lte"
      label="Due Before..."
      variant="outlined"
      resettable
      onKeyPress={(event) => event.preventDefault()}
    />,
    <DateInput
      source="dueDate.gte"
      label="Due After..."
      variant="outlined"
      resettable
      onKeyPress={(event) => event.preventDefault()}
    />,
  ];
};

const ListActions = (props) => {
  return (
    <TopToolbar>
      <FilterButton />
      <RefreshButton />
      <BackButton basePath="/reports" />
    </TopToolbar>
  );
};

// /reportInvoiceAudit
export const InvoiceAuditReport = (props) => {
  const classes = useStyles();
  const [defaultFilter] = useState({
    reloadYardId: window.appUser.reloadYardId,
  });
  const [defaultSearchFilter] = useState({
    reloadYardId: { eq: window.appUser?.reloadYardId },
  });

  // Include key & search to display default sort order.
  const defaultSort = {
    field: `{"key":"invoiceByActiveByStatusByDateByAccount", "search":"dueDate"}`,
    order: "DESC",
  };
  const defaultPerPage = 10;

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Invoices",
    defaultFilter: defaultFilter,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
    defaultSort: defaultSort,
    resource: "invoices",
  });

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Invoices" />} />
      <ListToolbar
        {...props}
        filters={getListFilters()}
        actions={<ListActions />}
      />

      <Card
        className={classnames(classes.root, {
          [classes.bulkActionsDisplayed]: listContext.selectedIds.length > 0,
        })}
      >
        <BulkActionsToolbar>
          <BulkExportButton
            {...props}
            exporter={exporter}
            color="default"
            icon={<ExportIcon />}
          />
        </BulkActionsToolbar>
        <InvoiceGrid {...props} {...listContext} />
        <PaginationToolbar {...props} />
      </Card>
    </ListContextProvider>
  );
};

const PaginationToolbar = (props) => {
  const classes = useStyles();
  const listContext = useListContext(props);

  var formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  const [totals, setTotals] = useState({ subtotal: 0, tax: 0, total: 0 });

  useEffect(() => {
    debugger;
    if (listContext?.ids?.length > 0 && listContext?.data) {
      let subtotal = 0,
        tax = 0,
        total = 0;

      listContext.ids?.forEach((id) => {
        subtotal += listContext.data[id].subtotal;
        tax += listContext.data[id].tax;
        total += listContext.data[id].total;
      });

      setTotals({ subtotal, tax, total });
    }
  }, [listContext]);

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Hidden mdDown>
        <Grid item md={5} lg={5}>
          <Typography className={classes.totals}>
            {`SUBTOTAL: ${formatter.format(
              totals.subtotal
            )}, TAX: ${formatter.format(totals.tax)}, TOTAL: ${formatter.format(
              totals.total
            )}`}
          </Typography>
        </Grid>
      </Hidden>
      <Grid item xs={12} md={7}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

const InvoiceGrid = ({ ids, page, ...props }) => {
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const hasBulkActions = true;

  return isXSmall ? (
    <SimpleList
      primaryText={(record) => record.accountName}
      secondaryText={(record) =>
        `Invoice No: ${record.invoiceNumber}    Amount $: ${record.totalAmount}`
      }
      tertiaryText={(record) => new Date(record.dueDate).toLocaleDateString()}
      leftIcon={(record) => <InvoiceStatusIcon record={record} />}
      rightIcon={() => (
        <ChevronRightIcon
          fontSize="large"
          color="secondary"
          style={{ marginLeft: 40, marginTop: 10 }}
        />
      )}
      linkType="edit"
    />
  ) : (
    <Datagrid
      {...props}
      rowClick="edit"
      expand={<InvoicePeak />}
      hasBulkActions={hasBulkActions}
    >
      <TextField source="accountName" label="Customer" sortable={true} />
      <NumberField
        source="total"
        label="Amount Due"
        sortable={true}
        options={{
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }}
      />
      <DateField
        source="dueDate"
        sortable={true}
        sortBy={`{"key":"invoiceByActiveByStatusByDateByAccount", "search":"dueDate"}`}
      />
      <InvoiceStatusIcon source="status" sortable={false} />
      <TextField source="poNumber" label="PO No." sortable={true} />
      <TextField source="invoiceNumber" label="Invoice No." sortable={true} />
    </Datagrid>
  );
};
